
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";

export function useAvatarManagement(userId: string | undefined) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const avatarUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMounted = useRef(true);

  // Set isMounted to false when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (avatarUpdateTimeoutRef.current) {
        clearTimeout(avatarUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Fetch existing avatar URL when component mounts
  useEffect(() => {
    const fetchAvatarUrl = async () => {
      if (!userId) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('id', userId)
          .single();
          
        if (error) throw error;
        if (data?.avatar_url && isMounted.current) setAvatarUrl(data.avatar_url);
      } catch (error: unknown) {
        console.error(
          'Error fetching avatar URL:',
          error instanceof Error ? error.message : error
        );
      }
    };
    
    fetchAvatarUrl();
  }, [userId]);

  // Update avatar URL in the database when it changes
  useEffect(() => {
    const updateAvatarInDb = async () => {
      if (!userId || isUploading) return;

      // Clear any existing timeout
      if (avatarUpdateTimeoutRef.current) {
        clearTimeout(avatarUpdateTimeoutRef.current);
      }

      // Set a timeout to update avatar after a delay to debounce multiple changes
      avatarUpdateTimeoutRef.current = setTimeout(async () => {
        try {
          console.log('Updating avatar in database:', { userId, avatarUrl });

          // Get current user to verify authentication
          const { data: { user }, error: authError } = await supabase.auth.getUser();
          if (authError) {
            console.error('Auth error:', authError);
            throw authError;
          }

          console.log('Current authenticated user:', user?.id, 'Target userId:', userId);

          const { error } = await supabase
            .from('profiles')
            .update({
              avatar_url: avatarUrl,
              updated_at: new Date().toISOString(),
            })
            .eq('id', userId);

          if (error) {
            console.error('Database update error:', error);
            throw error;
          }

          console.log('Avatar URL updated successfully in database');
        } catch (error: unknown) {
          console.error(
            'Error updating avatar in database:',
            error instanceof Error ? error.message : error
          );
          if (isMounted.current) {
            toast({
              variant: "destructive",
              title: "Error updating profile",
              description: error instanceof Error ? error.message : String(error),
            });
          }
        }
      }, 500); // 500ms debounce time
    };

    updateAvatarInDb();

    // Cleanup function
    return () => {
      if (avatarUpdateTimeoutRef.current) {
        clearTimeout(avatarUpdateTimeoutRef.current);
      }
    };
  }, [userId, avatarUrl, isUploading, toast]);

  const onAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) {
      return;
    }

    try {
      setIsUploading(true);

      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Create a unique file name to prevent collisions
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`; // Use user folder structure for RLS

      // Upload the file to Supabase storage
      console.log('Uploading avatar:', { userId, filePath, fileSize: file.size });
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true // Allow overwriting existing files
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        throw uploadError;
      }

      // Get the public URL for the uploaded file
      const { data: assetData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (!assetData?.publicUrl) {
        throw new Error('No public URL');
      }

      if (isMounted.current) {
        setAvatarUrl(assetData.publicUrl);
      
        toast({
          title: "Avatar uploaded",
          description: "Your avatar has been updated successfully.",
        });
      }
      
    } catch (error: unknown) {
      console.error(
        'Error uploading avatar:',
        error instanceof Error ? error.message : error
      );
      if (isMounted.current) {
        toast({
          title: "Error uploading avatar",
          description: error instanceof Error ? error.message : String(error),
          variant: "destructive"
        });
      }
    } finally {
      if (isMounted.current) {
        setIsUploading(false);
      }
    }
  };

  return {
    avatarUrl,
    setAvatarUrl,
    isUploading,
    setIsUploading,
    onAvatarChange
  };
}
